import { z } from "zod";
import { ExtraCategories, ExtraCategory } from "../ExtraCategory.enum";
import { CategoryExtraLine } from "./CategoryExtraLine";
import {
  IssueCode,
  IssueField,
  IssueSeverity,
  IssueTarget,
  ValidationIssue,
} from "./ValidationIssue";

/** Options of the *Included* and *Maximum* dropdowns. The server checks them. */
export const INCLUDED_RANGE = Object.freeze({ min: 0, max: 10 });
export const MAX_RANGE = Object.freeze({ min: 1, max: 10 });

const CategoryLimitSchema = z.object({
  category: z
    .enum(ExtraCategory)
    .refine(ExtraCategories.isAssignable, { message: "A block needs a category" }),
  enabled: z.boolean().default(false),
  // 0 is a real value, distinct from undefined.
  included: z.number().int().optional(),
  max: z.number().int().optional(),
  /** In the property extras order, which this page cannot change. */
  extras: z
    .array(z.custom<CategoryExtraLine>(value => value instanceof CategoryExtraLine))
    .default([]),
  tickedExtraTickers: z.array(z.string().min(1)).default([]),
});

export type CategoryLimitProps = z.input<typeof CategoryLimitSchema>;

/**
 * One block of the *Limits per category* section: the extras the backend lists for the
 * category plus the values and ticks of the accommodation. It holds the only client-side
 * rules: the first free extra text, the pre-checks before saving and the inline warnings.
 * Which ticks are effective is decided by the backend and never recomputed here.
 */
export class CategoryLimit {
  public readonly category: ExtraCategory;
  public readonly enabled: boolean;
  public readonly included?: number;
  public readonly max?: number;
  public readonly extras: readonly CategoryExtraLine[];
  private readonly ticked: ReadonlySet<string>;

  private constructor(props: z.infer<typeof CategoryLimitSchema>) {
    this.category = props.category;
    this.enabled = props.enabled;
    this.included = props.included;
    this.max = props.max;
    this.extras = Object.freeze([...props.extras]);
    // Only active lines of this block can be ticked.
    const tickable = new Set(
      props.extras.filter(line => line.isTickable()).map(line => line.ticker)
    );
    this.ticked = new Set(props.tickedExtraTickers.filter(ticker => tickable.has(ticker)));
  }

  static of(props: CategoryLimitProps): CategoryLimit {
    return new CategoryLimit(CategoryLimitSchema.parse(props));
  }

  /** Turning the toggle off keeps every value. */
  withEnabled(enabled: boolean): CategoryLimit {
    return CategoryLimit.of({ ...this.props(), enabled });
  }

  withIncluded(included: number | undefined): CategoryLimit {
    return CategoryLimit.of({ ...this.props(), included });
  }

  withMax(max: number | undefined): CategoryLimit {
    return CategoryLimit.of({ ...this.props(), max });
  }

  /** A tick on an inactive or unknown line is ignored. */
  withTicked(extraTicker: string, ticked: boolean): CategoryLimit {
    const others = this.tickedExtraTickers.filter(ticker => ticker !== extraTicker);
    return CategoryLimit.of({
      ...this.props(),
      tickedExtraTickers: ticked ? [...others, extraTicker] : others,
    });
  }

  line(extraTicker: string): CategoryExtraLine | undefined {
    return this.extras.find(line => line.ticker === extraTicker);
  }

  isTicked(line: CategoryExtraLine): boolean {
    return this.ticked.has(line.ticker);
  }

  tickedLines(): CategoryExtraLine[] {
    return this.extras.filter(line => this.isTicked(line));
  }

  /** In list order, as sent in the request. */
  get tickedExtraTickers(): string[] {
    return this.tickedLines().map(line => line.ticker);
  }

  /** First free extra: the first line, in list order, ticked, active and covering a line. */
  firstFreeExtra(): CategoryExtraLine | undefined {
    return this.extras.find(line => this.isTicked(line) && line.isSellableWhenTicked());
  }

  /**
   * Included and maximum set, included ≤ maximum and one active extra ticked, for a block that
   * is on. A block that is off is never validated.
   */
  preCheck(): ValidationIssue[] {
    if (!this.enabled) {
      return [];
    }
    const { included, max } = this;
    const issues: ValidationIssue[] = [];
    if (included === undefined) {
      issues.push(
        this.blockingIssue(IssueCode.CATEGORY_LIMITS_INCLUDED_REQUIRED, IssueField.INCLUDED)
      );
    }
    if (max === undefined) {
      issues.push(this.blockingIssue(IssueCode.CATEGORY_LIMITS_MAX_REQUIRED, IssueField.MAX));
    }
    if (included !== undefined && max !== undefined && included > max) {
      issues.push(
        this.blockingIssue(IssueCode.CATEGORY_LIMITS_INCLUDED_GREATER_THAN_MAX, IssueField.INCLUDED)
      );
    }
    if (this.ticked.size === 0) {
      issues.push(this.blockingIssue(IssueCode.CATEGORY_LIMITS_NO_ACTIVE_EXTRA, IssueField.EXTRAS));
    }
    return issues;
  }

  /** One warning per ticked line with no inventory line, if on. */
  inlineInventoryWarnings(): ValidationIssue[] {
    if (!this.enabled) {
      return [];
    }
    return this.tickedLines()
      .filter(line => !line.coversInventoryLine)
      .map(line =>
        ValidationIssue.of({
          code: IssueCode.CATEGORY_LIMITS_EXTRA_NO_INVENTORY_LINE,
          severity: IssueSeverity.WARNING,
          target: IssueTarget.of({
            category: this.category,
            field: IssueField.EXTRAS,
            extraTicker: line.ticker,
          }),
          params: { extra: line.name },
        })
      );
  }

  private props(): CategoryLimitProps {
    return {
      category: this.category,
      enabled: this.enabled,
      included: this.included,
      max: this.max,
      extras: [...this.extras],
      tickedExtraTickers: this.tickedExtraTickers,
    };
  }

  private blockingIssue(code: IssueCode, field: IssueField): ValidationIssue {
    return ValidationIssue.of({
      code,
      severity: IssueSeverity.BLOCKING,
      target: IssueTarget.of({ category: this.category, field }),
      params: { category: this.category },
    });
  }
}
